import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { AutocompleteField } from '../shared/AutocompleteField';
import { CounterPopoverField } from '../shared/CounterPopoverField';
import { MonthYearField } from '../shared/MonthYearField';
import { FieldShell } from '../shared/FieldShell';
import {
  hasAvailableDates,
  isMonthAvailable,
  pickClosestAvailableMonth,
} from '../shared/availableMonths';
import { usePackageAvailableDates } from './hooks/usePackageAvailableDates';
import { usePackageDestinations, usePackageOrigins } from './hooks/usePackagePlaces';
import {
  EMPTY_PACKAGE_SEARCH,
  PackageSearchValues,
  buildPackageSearchQuery,
  parsePackageSearchParams,
  placeIdsParam,
} from './packageSearchQuery';
import {
  PackageSearchErrors,
  summarizeMissingFields,
  validatePackageSearch,
} from './validatePackageSearch';
import type { PackagePlace } from './api/packages.types';
import type { SearcherFormProps } from '../types';
import '../shared/SearchFields.css';

const MAX_ADULTS = 3;

function placeLabel(place: PackagePlace): string {
  return place.name;
}

function placeKey(place: PackagePlace): string {
  return place.ids.join(',');
}

function passengersPreview(adults: number): string {
  return adults === 1 ? '1 pasajero' : `${adults} pasajeros`;
}

/**
 * Real packages search, rendered inside the branding hero's search card.
 *
 * Ports the legacy PackageSearch + PackageFilterForm behavior (origin →
 * destination → available departure month → travellers, then navigate to
 * `/buscarxpaquetes`) onto typed state and this feature's own field components.
 */
const PackageSearchForm: React.FC<SearcherFormProps> = ({
  isMobile = false,
  onSearchComplete,
  onSearchStart,
}) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { registerOnSearch } = useGlobal();

  const urlValues = useMemo(
    () =>
      pathname.replace(/\/$/, '') === ROUTE_KEYS.BUSCAR_X_PAQUETES && search
        ? parsePackageSearchParams(search)
        : EMPTY_PACKAGE_SEARCH,
    [pathname, search],
  );

  const [values, setValues] = useState<PackageSearchValues>(urlValues);
  const [errors, setErrors] = useState<PackageSearchErrors>({});
  const [formMessage, setFormMessage] = useState('');
  const autoPickedForRef = useRef<string>('');

  useEffect(() => {
    setValues(urlValues);
  }, [urlValues]);

  const originId = placeIdsParam(values.origin);
  const destinationId = placeIdsParam(values.destination);

  const origins = usePackageOrigins();
  const destinations = usePackageDestinations(originId);
  const dates = usePackageAvailableDates(originId, destinationId);

  const selectedMonth = useMemo(
    () => (values.month && values.year ? { month: values.month, year: values.year } : null),
    [values.month, values.year],
  );

  // Land on the closest offered departure month as soon as availability arrives,
  // unless the URL (or the user) already picked one that is still offered.
  useEffect(() => {
    const pairKey = `${originId}|${destinationId}`;
    if (!hasAvailableDates(dates.availableDates) || autoPickedForRef.current === pairKey) return;

    autoPickedForRef.current = pairKey;

    if (isMonthAvailable(dates.availableDates, selectedMonth)) return;

    const picked = pickClosestAvailableMonth(dates.availableDates);
    if (!picked) return;

    setValues((prev) => ({ ...prev, month: picked.month, year: picked.year }));
    setErrors((prev) => ({ ...prev, dates: undefined }));
  }, [dates.availableDates, destinationId, originId, selectedMonth]);

  const handleOriginSelect = useCallback((origin: PackagePlace | null) => {
    autoPickedForRef.current = '';
    setValues((prev) => ({ ...prev, origin, destination: null, month: null, year: null }));
    setErrors((prev) => ({ ...prev, origin: undefined, destination: undefined, dates: undefined }));
  }, []);

  const handleDestinationSelect = useCallback((destination: PackagePlace | null) => {
    autoPickedForRef.current = '';
    setValues((prev) => ({ ...prev, destination, month: null, year: null }));
    setErrors((prev) => ({ ...prev, destination: undefined, dates: undefined }));
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validatePackageSearch(values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setFormMessage(summarizeMissingFields(nextErrors));
      return;
    }

    if (!hasAvailableDates(dates.availableDates)) {
      setErrors({ dates: 'No hay fechas disponibles para este destino' });
      setFormMessage('No hay fechas disponibles para este destino');
      return;
    }

    setErrors({});
    setFormMessage('');
    onSearchStart?.();

    const query = buildPackageSearchQuery(values, {
      cache: getCacheStatus(),
      refreshToken: Date.now(),
    });

    history.push(`${ROUTE_KEYS.BUSCAR_X_PAQUETES}${query}`);
    registerOnSearch?.();
    onSearchComplete?.();
  };

  const isSubmitDisabled = dates.isLoading;

  return (
    <form
      aria-label='Buscador de paquetes'
      className={['searcher-form', isMobile && 'searcher-form--mobile'].filter(Boolean).join(' ')}
      noValidate
      onSubmit={handleSubmit}
    >
      <FieldShell
        error={errors.origin}
        errorId='package-origin-error'
        glyph='⌖'
        htmlFor='package-origin'
        label='Origen'
      >
        <AutocompleteField<PackagePlace>
          errorId='package-origin-error'
          getOptionKey={placeKey}
          getOptionLabel={placeLabel}
          hasError={Boolean(errors.origin)}
          id='package-origin'
          isLoading={origins.isLoading}
          loadingText='Cargando orígenes…'
          onSelect={handleOriginSelect}
          options={origins.places}
          placeholder='¿Desde dónde?'
          value={values.origin}
        />
      </FieldShell>

      <FieldShell
        error={errors.destination}
        errorId='package-destination-error'
        glyph='⌖'
        htmlFor='package-destination'
        label='Destino'
      >
        <AutocompleteField<PackagePlace>
          disabled={!values.origin}
          errorId='package-destination-error'
          getOptionKey={placeKey}
          getOptionLabel={placeLabel}
          hasError={Boolean(errors.destination)}
          id='package-destination'
          isLoading={destinations.isLoading}
          loadingText='Cargando destinos…'
          onSelect={handleDestinationSelect}
          options={destinations.places}
          placeholder='¿Hasta dónde?'
          value={values.destination}
        />
      </FieldShell>

      <FieldShell
        error={errors.dates || dates.error}
        errorId='package-dates-error'
        glyph='▦'
        htmlFor='package-dates'
        label='Fecha'
      >
        <MonthYearField
          availableDates={dates.availableDates}
          errorId='package-dates-error'
          hasError={Boolean(errors.dates)}
          id='package-dates'
          isLoading={dates.isLoading}
          onChange={({ month, year }) => {
            setValues((prev) => ({ ...prev, month, year }));
            setErrors((prev) => ({ ...prev, dates: undefined }));
          }}
          value={selectedMonth}
        />
      </FieldShell>

      <FieldShell
        error={errors.passengers}
        errorId='package-passengers-error'
        glyph='♙'
        htmlFor='package-passengers'
        label='Pasajeros'
      >
        <CounterPopoverField
          counters={[{ key: 'adults', label: 'Adultos', max: MAX_ADULTS, min: 1 }]}
          disabled={!values.origin || !values.destination}
          errorId='package-passengers-error'
          hasError={Boolean(errors.passengers)}
          id='package-passengers'
          onChange={(_key, adults) => setValues((prev) => ({ ...prev, adults }))}
          previewText={passengersPreview(values.adults)}
          triggerLabel='Cantidad de pasajeros'
          values={{ adults: values.adults }}
        />
      </FieldShell>

      <button className='searcher-form__submit' disabled={isSubmitDisabled} type='submit'>
        Buscar
      </button>

      {formMessage && (
        <p aria-live='assertive' className='sr-only' role='alert'>
          {formMessage}
        </p>
      )}
    </form>
  );
};

export default PackageSearchForm;
